import { defineStore } from 'pinia'
import { ComponentStatus } from '@/consts/component-status'
import { MessagesApi } from '@/api/modules/message'
import { ref } from 'vue'
import { Role, TMessageContent } from '@/ui/widget/chat/types'
import { IMessage, IMessageBody } from '@/api/modules/message/types'
import { useI18n } from 'vue-i18n'

export const useChatHistoryStore = defineStore('chatHistoryStore', () => {
  const { t } = useI18n()

  const messageHistory = ref<TMessageContent[]>([])
  const lastMessage = ref()
  const hasMmore = ref(false)

  const retrySendMessage = async (id: string) => await MessagesApi.retrySendMessage(id)

  const getMessageHistoryById = (id: string) =>
    messageHistory.value.find((m) => m.custom?.message_id === id)

  const removeMessageHistoryById = (id: string) =>
    (messageHistory.value = messageHistory.value.filter(
      (m) => m.custom.message_id !== id || m.custom?.message_id !== id,
    ))

  const createHtmlMessage = ({ status, message_id, text, html }: any) =>
    `<div data-status='${status}' data-id='${message_id}'>${text}</div>`

  const createFilesData = (attachments: []) =>
    (attachments || []).map((a: any) => ({
      name: a.filename,
      src: a.download_url,
      type: a.kind.startsWith('image') ? 'image' : 'any',
    }))

  const createChatMessageData = (message: any /* IMessage */) => ({
    role:
      message.author === 'assistant'
        ? Role.Ai
        : message.author === Role.AiActions
          ? Role.AiActions
          : Role.User,
    ...(message.author !== Role.AiActions
      ? {
          text:
            message.status === 'processing'
              ? message.body.text || message.body.html || t('ui.chat-dialogs-processing')
              : message.body.text || message.body.html
                ? createHtmlMessage({
                    status: message.status,
                    message_id: message.message_id,
                    text:
                      message.author === 'assistant'
                        ? '\n\n' + message.body.text
                        : message.body.text,
                    html: message.body.html,
                  })
                : undefined,
          html: undefined,
        }
      : {
          text: undefined,
          html: message.body.html,
        }),
    status: 'completed',
    message_id: message.message_id,
    files: createFilesData(message.body.attachments),
    custom: {
      message_id: message.message_id,
      o_text: message.status !== 'processing' ? message.body.text : '',
      o_html: message.status !== 'processing' ? message.body.html : '',
      retry: false,
    },
  })

  const createActionData = (message: any): IMessage => {
    const { actions, ...data } = message.body

    return {
      ...message,
      author: Role.AiActions,
      body: {
        ...data,
        text: undefined,
        attachments: [],
        html: `
          <div class="deep-chat-temporary-message">
            ${(message.body.actions || []).map((a: any) => `<button class="deep-chat-button action-button" style="margin-top: 5px" data-action="${a.action}" onclick="window.action(this)">${a.text}</button>`).join('')}
          </div>`,
      },
    }
  }

  const mapper = (messages: IMessage[]): TMessageContent[] => {
    const mappedMessages = Array<any>()

    ;(messages || []).forEach((m: IMessage, index: number) => {
      mappedMessages.push(createChatMessageData(m))
      if (m.status === 'error' && index === messages.length - 1) {
        mappedMessages.push({ error: 'error', custom: { message_id: m.message_id } })
      }
    })

    return mappedMessages
  }

  const getThreadMessages = async (
    thread_id: string,
    before_id?: string,
  ): Promise<Array<TMessageContent | boolean>> => {
    const { data } = await MessagesApi.getThreadMessages({
      thread_id,
      before_id,
    })

    hasMmore.value = data.has_more

    lastMessage.value =
      data.items && data.items.length > 0 ? data.items[data.items.length - 1] : null

    if (
      lastMessage &&
      lastMessage.value &&
      lastMessage.value?.body?.actions &&
      lastMessage.value?.body?.actions?.length > 0
    ) {
      const mapped = [...data.items, createActionData(lastMessage.value)]
      messageHistory.value = mapper(mapped)

      return messageHistory.value
    }

    const mappedMessages = mapper(data.items)

    if (before_id) {
      messageHistory.value = [...mappedMessages, ...messageHistory.value]
    } else {
      messageHistory.value = mappedMessages
    }

    return [...messageHistory.value, !data.has_more]
  }

  const clearThreadMessages = () => (messageHistory.value = [])

  const addNewMessage = (newMessage: any) =>
    (messageHistory.value = [...messageHistory.value, newMessage])

  const updateMessage = (update: Partial<TMessageContent>, message_id: string) => {
    messageHistory.value = messageHistory.value.map((item: TMessageContent) =>
      item.custom?.message_id === message_id ? { ...item, ...update } : item,
    )
  }

  const setLastMessage = (message: any) => {
    lastMessage.value = message
  }

  return {
    messageHistory,
    lastMessage,
    hasMmore,

    removeMessageHistoryById,
    createHtmlMessage,
    createActionData,
    retrySendMessage,
    getMessageHistoryById,
    setLastMessage,
    addNewMessage,
    updateMessage,
    getThreadMessages,
    clearThreadMessages,
    createChatMessageData,
  }
})
